import React, { useEffect, useState, useCallback } from "react";
import { Dropdown } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  Brain, Plus, MoreHorizontal, Upload, Download, Pencil, Trash2,
  SlidersHorizontal,
} from "lucide-react";
import * as XLSX from "xlsx";
import apiClient from "../../utils/apiClient";
import { techStacksList } from "../../utils/common";
import DataTable from "../Common/DataTable";
import {
  PageHeader, AppButton, AppBadge, AppCard, AppCardContent,
  SearchInput, FilterBar, FilterSelect, ConfirmModal, EmptyState,
} from "../ui";
import { FilterModal, FilterGroup } from "../ui/FilterModal";
import CommonToaster from "../Common/CommonToaster";
import AIQuestionsImportModal from "./AIQuestionsImportModal";
import { useAIBankHeaderActions } from "./aiBankHeaderContext";
import "./AIQuestionsList.scss";

const defaultTechStacks = techStacksList.filter((t) => t !== "");

const SORT_OPTIONS = [
  { value: "newest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
  { value: "question-asc", label: "Question A → Z" },
  { value: "question-desc", label: "Question Z → A" },
];

const DIFFICULTY_OPTIONS = [
  { value: "", label: "All Difficulties" },
  { value: "easy", label: "Easy" },
  { value: "medium", label: "Medium" },
  { value: "hard", label: "Hard" },
];

const DIFFICULTY_BADGE_COLOR = {
  easy: "emerald",
  medium: "amber",
  hard: "rose",
};

const PAGE_SIZE = 10;

const AIQuestionsList = ({ embedded = false }) => {
  const navigate = useNavigate();

  const [questions, setQuestions] = useState([]);
  const [filters, setFilters] = useState({
    techStack: "", difficulty: "", search: "", sort: "newest",
  });
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(PAGE_SIZE);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);

  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [techStacks, setTechStacks] = useState(defaultTechStacks);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const showToast = (message, type = "success") =>
    setToast({ show: true, message, type });

  // ── Data fetching ──────────────────────────────────────────────
  const fetchTechStacks = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/ai/ai-questions/tech-stacks");
      const list = Array.isArray(data?.techStacks) ? data.techStacks : [];
      setTechStacks(list);
    } catch {
      /* keep defaults */
    }
  }, []);

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/ai/ai-questions/list", {
        params: { page, limit: pageSize, ...filters },
      });
      setQuestions(data.questions || []);
      setTotalRecords(data.total || 0);
    } catch {
      setQuestions([]);
      setTotalRecords(0);
    }
    setLoading(false);
  }, [page, pageSize, filters]);

  useEffect(() => { fetchTechStacks(); }, [fetchTechStacks]);
  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  // Debounce search input → filters
  useEffect(() => {
    if (searchInput === filters.search) return;
    const t = setTimeout(() => {
      setFilters((f) => ({ ...f, search: searchInput }));
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // ── Filter helpers ─────────────────────────────────────────────
  const techStackOptions = [
    { value: "", label: "All Tech Stacks" },
    ...techStacks.map((t) => ({ value: t, label: t })),
  ];

  const handleResetFilters = () => {
    setFilters({ techStack: "", difficulty: "", search: "", sort: "newest" });
    setSearchInput("");
    setPage(1);
  };

  const activeFilterCount =
    (filters.techStack ? 1 : 0) +
    (filters.difficulty ? 1 : 0) +
    (filters.sort !== "newest" ? 1 : 0);

  // ── Export ─────────────────────────────────────────────────────
  const handleExport = async () => {
    setExporting(true);
    try {
      const { data } = await apiClient.get("/ai/ai-questions/export", {
        params: {
          techStack: filters.techStack || undefined,
          difficulty: filters.difficulty || undefined,
          search: filters.search || undefined,
        },
      });
      const rows = (data?.questions || []).map((q) => ({
        question: q.question,
        techStack: q.techStack,
        difficulty: q.difficulty,
        createdAt: q.createdAt ? new Date(q.createdAt).toISOString() : "",
      }));
      if (rows.length === 0) {
        showToast("No questions to export for the current filters.", "warning");
        return;
      }
      const ws = XLSX.utils.json_to_sheet(rows, {
        header: ["question", "techStack", "difficulty", "createdAt"],
      });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "AI Questions");
      const date = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `ai-questions-${date}.xlsx`);
      showToast(`Exported ${rows.length} question(s)`, "success");
      if (data?.capped) {
        showToast("Export was capped at 5000 rows. Apply filters to narrow.", "warning");
      }
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to export questions.", "error");
    }
    setExporting(false);
  };

  const handleImported = () => {
    fetchQuestions();
    fetchTechStacks();
  };

  // ── Delete ─────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteTarget?._id) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/ai/ai-questions/${deleteTarget._id}`);
      showToast("Question deleted", "success");
      setDeleteTarget(null);
      fetchQuestions();
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to delete question.", "error");
    }
    setDeleting(false);
  };

  // ── Table columns ──────────────────────────────────────────────
  const columns = [
    {
      key: "index",
      label: "#",
      width: "56px",
      render: (_, index) => (
        <span className="aiq-row-index">{(page - 1) * pageSize + index + 1}</span>
      ),
    },
    {
      key: "question",
      label: "Question",
      render: (q) => (
        <div className="aiq-cell-question" title={q.question}>
          {q.question}
        </div>
      ),
    },
    {
      key: "techStack",
      label: "Category",
      width: "180px",
      render: (q) => (
        q.techStack ? (
          <AppBadge variant="secondary" size="sm">{q.techStack}</AppBadge>
        ) : <span className="text-muted">—</span>
      ),
    },
    {
      key: "difficulty",
      label: "Difficulty",
      width: "120px",
      render: (q) => (
        q.difficulty ? (
          <AppBadge color={DIFFICULTY_BADGE_COLOR[q.difficulty.toLowerCase()] || "blue"} size="sm">
            {q.difficulty.charAt(0).toUpperCase() + q.difficulty.slice(1)}
          </AppBadge>
        ) : <span className="text-muted">—</span>
      ),
    },
    {
      key: "actions",
      label: "",
      width: "56px",
      render: (q) => (
        <div className="aiq-row-actions" onClick={(e) => e.stopPropagation()}>
          <Dropdown align="end">
            <Dropdown.Toggle
              as="button"
              type="button"
              bsPrefix="aiq-row-trigger"
              aria-label="Row actions"
            >
              <MoreHorizontal size={16} />
            </Dropdown.Toggle>
            <Dropdown.Menu className="aiq-row-menu">
              <Dropdown.Item onClick={() => navigate(`/ai-questions/edit/${q._id}`)}>
                <Pencil size={14} className="me-2" /> Edit
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item
                onClick={() => setDeleteTarget(q)}
                className="text-danger"
              >
                <Trash2 size={14} className="me-2" /> Delete
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      ),
    },
  ];

  // ── Header actions (Import → Export → New Question) ────────────
  const primaryActions = (
    <>
      <AppButton variant="outline" size="sm" onClick={() => setShowImportModal(true)}>
        <Upload size={14} /> <span>Import</span>
      </AppButton>
      <AppButton variant="outline" size="sm" onClick={handleExport} disabled={exporting}>
        <Download size={14} /> <span>{exporting ? "Exporting..." : "Export"}</span>
      </AppButton>
      <AppButton variant="default" size="sm" onClick={() => navigate("/ai-questions/new")}>
        <Plus size={14} /> <span>New Question</span>
      </AppButton>
    </>
  );

  // When embedded inside AIBankPage, push actions into the parent PageHeader.
  useAIBankHeaderActions(embedded ? primaryActions : null, [embedded, exporting]);

  return (
    <div className="aiq-page">
      {!embedded && (
        <PageHeader
          icon={Brain}
          title="AI Questions"
          description="Manage AI-powered interview questions"
          actions={primaryActions}
        />
      )}

      {/* Filters */}
      <FilterBar
        className="aiq-filters d-none d-md-flex"
        actions={
          <SearchInput
            value={searchInput}
            onChange={setSearchInput}
            placeholder="Search questions..."
            className="aiq-search"
          />
        }
      >
        <FilterSelect
          label="Category"
          value={filters.techStack}
          onChange={(v) => { setFilters((f) => ({ ...f, techStack: v })); setPage(1); }}
          options={techStackOptions}
        />
        <FilterSelect
          label="Difficulty"
          value={filters.difficulty}
          onChange={(v) => { setFilters((f) => ({ ...f, difficulty: v })); setPage(1); }}
          options={DIFFICULTY_OPTIONS}
        />
        <FilterSelect
          label="Sort"
          value={filters.sort}
          onChange={(v) => { setFilters((f) => ({ ...f, sort: v })); setPage(1); }}
          options={SORT_OPTIONS}
        />
      </FilterBar>

      {/* Mobile-only search + filter trigger (filters live in modal) */}
      <div className="aiq-mobile-toolbar d-md-none">
        <SearchInput
          value={searchInput}
          onChange={setSearchInput}
          placeholder="Search questions..."
        />
        <AppButton
          variant="outline"
          className="aiq-mobile-filter"
          onClick={() => setShowFilterModal(true)}
        >
          <SlidersHorizontal size={16} />
          {activeFilterCount > 0 && (
            <AppBadge variant="default" size="sm" className="ms-1">{activeFilterCount}</AppBadge>
          )}
        </AppButton>
      </div>

      {/* Table */}
      <AppCard className="aiq-table-card">
        <AppCardContent className="aiq-table-card__content">
          {!loading && questions.length === 0 ? (
            <EmptyState
              icon={<Brain size={36} />}
              title={
                filters.search || filters.techStack || filters.difficulty
                  ? "No matching questions"
                  : "No AI questions yet"
              }
              description={
                filters.search || filters.techStack || filters.difficulty
                  ? "Try clearing filters or use a different search term."
                  : "Create your first AI interview question to get started."
              }
              action={
                <AppButton variant="default" onClick={() => navigate("/ai-questions/new")}>
                  <Plus size={16} /> New Question
                </AppButton>
              }
            />
          ) : (
            <DataTable
              data={questions}
              columns={columns}
              loading={loading}
              hoverable
              pagination={{
                page,
                pageSize,
                total: totalRecords,
                onPageChange: setPage,
              }}
              emptyMessage="No AI questions found"
            />
          )}
        </AppCardContent>
      </AppCard>

      {/* Mobile filter modal */}
      <FilterModal
        show={showFilterModal}
        onHide={() => setShowFilterModal(false)}
        onReset={handleResetFilters}
      >
        <FilterGroup label="Category">
          <select
            value={filters.techStack}
            onChange={(e) => { setFilters((f) => ({ ...f, techStack: e.target.value })); setPage(1); }}
          >
            {techStackOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </FilterGroup>
        <FilterGroup label="Difficulty">
          <select
            value={filters.difficulty}
            onChange={(e) => { setFilters((f) => ({ ...f, difficulty: e.target.value })); setPage(1); }}
          >
            {DIFFICULTY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </FilterGroup>
        <FilterGroup label="Sort By">
          <select
            value={filters.sort}
            onChange={(e) => { setFilters((f) => ({ ...f, sort: e.target.value })); setPage(1); }}
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </FilterGroup>
      </FilterModal>

      <AIQuestionsImportModal
        show={showImportModal}
        onHide={() => setShowImportModal(false)}
        onImported={handleImported}
      />

      <ConfirmModal
        show={!!deleteTarget}
        onHide={() => !deleting && setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete question?"
        message={
          <>
            <p className="mb-2">This will permanently remove the question:</p>
            <p className="mb-0 fw-medium">{deleteTarget?.question?.slice(0, 160)}{deleteTarget?.question?.length > 160 ? "…" : ""}</p>
          </>
        }
        confirmText={deleting ? "Deleting..." : "Delete"}
        variant="danger"
        loading={deleting}
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default AIQuestionsList;
